import * as React from "react";
import { cn } from "@/lib/utils";

interface FloatingLabelInputProps extends React.ComponentProps<"input"> {
  /** The resting label — sits centered like a placeholder when the field is
   *  empty & unfocused, then shrinks and rises to the top on focus / when a
   *  value is present, revealing the format `placeholder` below it. */
  label: string;
}

/**
 * Floating-label input. CSS-only via the `peer` + `:placeholder-shown`
 * pattern: the native `placeholder` holds the format hint (e.g.
 * "0000 0000 0000 0000") but is kept transparent until focus, so the empty
 * state shows only the centered `label`. The label floats up on focus or
 * when the field has a value (`:not(:placeholder-shown)`).
 *
 * Taller than the base Input (h-14) so the floated label and the value have
 * room to stack. Matches the design system's golden focus border.
 */
const FloatingLabelInput = React.forwardRef<
  HTMLInputElement,
  FloatingLabelInputProps
>(({ className, label, id, ...props }, ref) => {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;

  return (
    <div className="relative">
      <input
        id={inputId}
        ref={ref}
        className={cn(
          "peer bg-white/08 h-14 w-full rounded-10 border border-input px-4 pt-5 pb-1.5 text-base text-white shadow-sm transition-colors",
          "placeholder:text-transparent focus:placeholder:text-[#58585D]",
          "focus-visible:outline-none focus:bg-[#101017] focus:border-[#FBC76C]",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...props}
      />
      <label
        htmlFor={inputId}
        className={cn(
          "pointer-events-none absolute left-4 leading-none text-[#58585D] transition-all duration-300 ease-out",
          // resting: vertically centered, matches input text size
          "top-1/2 -translate-y-1/2 text-base",
          // floated (focused): small, pinned near the top, accent color
          "peer-focus:top-1 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:text-orange-100",
          // floated (has value): small, pinned near the top
          "peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px]"
        )}
      >
        {label}
      </label>
    </div>
  );
});

FloatingLabelInput.displayName = "FloatingLabelInput";

export default FloatingLabelInput;
